import {
  AddTeamMemberData,
  ApiResponse,
  ProjectMember,
  RolePermissions,
  TeamMemberInvitation,
  TeamPermission,
  TeamUser,
  UpdateTeamMemberData,
} from '@/types';
import { api } from './api';
import { invitationService } from './invitationService';

export const teamService = {
  // Get project team members
  getProjectTeam: async (
    projectId: string,
    options: any = {}
  ): Promise<ApiResponse<ProjectMember[]>> => {
    try {
      const queryParams = new URLSearchParams();

      // Add query parameters
      if (options.page) queryParams.append('page', options.page.toString());
      if (options.limit) queryParams.append('limit', options.limit.toString());
      if (options.search) queryParams.append('search', options.search);
      if (options.role) queryParams.append('role', options.role);
      if (options.status) queryParams.append('status', options.status);
      if (options.sortBy) queryParams.append('sortBy', options.sortBy);
      if (options.sortOrder) queryParams.append('sortOrder', options.sortOrder);
      if (options.includeInactive)
        queryParams.append('includeInactive', options.includeInactive.toString());

      const url = `/projects/${projectId}/team${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await api.get(url);

      if (response.data.success) {
        // Process the team data to ensure proper structure
        const rawData = response.data.data?.data || response.data.data || [];

        const processedData = rawData.map((member: any) => ({
          ...member,
          id: member._id || member.id,
          user: {
            ...member.user,
            id: member.user._id || member.user.id,
            // Use firstName/lastName from backend
            firstName: member.user?.firstName || 'Unknown',
            lastName: member.user?.lastName || 'User',
            name:
              member.user?.name ||
              `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim(),
            company: member.user?.companyName || '',
            title: member.user?.companyType || '',
            expertise: [],
            isActive: member.user?.isActive ?? true,
          },
        }));

        return {
          success: true,
          data: processedData,
          message: response.data.message,
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to fetch team members',
        };
      }
    } catch (error: any) {
      console.error('Error fetching team members:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch team members',
      };
    }
  },

  // Get available roles and permissions
  getRolesAndPermissions: async (): Promise<
    ApiResponse<{ roles: RolePermissions[]; permissions: TeamPermission[] }>
  > => {
    try {
      // TODO: Temporarily bypassing configuration service to test modal
      // Get roles from configuration service
      // const roleOptions = await configurationService.getDropdownOptions('team-roles');
      throw new Error('Temporarily using fallback roles');

      // Transform configuration data to RolePermissions format
      // const roles: RolePermissions[] = roleOptions.map(option => ({
      //   role: option.value as TeamRole,
      //   defaultPermissions: option.metadata?.permissions || [],
      //   description: option.metadata?.description || '',
      //   canManageTeam: option.metadata?.canManageTeam || false,
      //   canEditProject: option.metadata?.canEditProject || false,
      //   canViewFinancials: option.metadata?.canViewFinancials || false,
      // }));

      // Define all available permissions (used in fallback)
      // const permissions: TeamPermission[] = [
      //   'view_project',
      //   'edit_documents',
      //   'manage_project',
      //   'manage_team',
      //   'view_financials',
      //   'manage_communications',
      //   'update_progress',
      //   'manage_safety',
      //   'upload_photos',
      //   'field_reports',
      //   'upload_drawings',
      //   'review_specifications',
      //   'design_review',
      //   'manage_inspections',
      //   'technical_review',
      //   'view_schedule',
      //   'submit_reports',
      //   'view_progress',
      //   'view_communications',
      //   'view_documents',
      //   'create_reports',
      //   'view_compliance',
      //   'admin_access',
      // ];

      // return {
      //   success: true,
      //   data: {
      //     roles,
      //     permissions,
      //   },
      // };
    } catch (error) {
      console.error('Failed to load roles and permissions:', error);
      console.log('Using fallback hardcoded roles...');

      // Fallback to hardcoded roles if configuration service fails
      return {
        success: true,
        data: {
          roles: [
            {
              role: 'project_manager',
              defaultPermissions: [
                'manage_project',
                'manage_team',
                'view_financials',
                'edit_documents',
                'manage_communications',
              ],
              description: 'Full project oversight and team management',
              canManageTeam: true,
              canEditProject: true,
              canViewFinancials: true,
            },
            {
              role: 'site_supervisor',
              defaultPermissions: [
                'view_project',
                'update_progress',
                'manage_safety',
                'upload_photos',
              ],
              description: 'On-site supervision and progress tracking',
              canManageTeam: false,
              canEditProject: false,
              canViewFinancials: false,
            },
            {
              role: 'architect',
              defaultPermissions: [
                'edit_documents',
                'view_project',
                'upload_drawings',
                'review_specifications',
              ],
              description: 'Design oversight and documentation',
              canManageTeam: false,
              canEditProject: true,
              canViewFinancials: false,
            },
            {
              role: 'engineer',
              defaultPermissions: [
                'edit_documents',
                'view_project',
                'manage_inspections',
                'review_specifications',
              ],
              description: 'Technical oversight and quality assurance',
              canManageTeam: false,
              canEditProject: true,
              canViewFinancials: false,
            },
            {
              role: 'contractor',
              defaultPermissions: [
                'view_project',
                'update_progress',
                'upload_photos',
                'view_schedule',
              ],
              description: 'Construction execution and progress updates',
              canManageTeam: false,
              canEditProject: false,
              canViewFinancials: false,
            },
            {
              role: 'client',
              defaultPermissions: ['view_project', 'view_progress', 'view_communications'],
              description: 'Project visibility and communication access',
              canManageTeam: false,
              canEditProject: false,
              canViewFinancials: true,
            },
            {
              role: 'viewer',
              defaultPermissions: ['view_project'],
              description: 'Read-only access to project information',
              canManageTeam: false,
              canEditProject: false,
              canViewFinancials: false,
            },
          ],
          permissions: [
            'view_project',
            'edit_documents',
            'manage_project',
            'manage_team',
            'view_financials',
            'manage_communications',
            'update_progress',
            'manage_safety',
            'upload_photos',
            'view_schedule',
            'upload_drawings',
            'review_specifications',
            'manage_inspections',
            'view_progress',
            'view_communications',
          ],
        },
      };
    }
  },

  // Add team member via invitation
  addTeamMember: async (
    projectId: string,
    memberData: AddTeamMemberData
  ): Promise<ApiResponse<ProjectMember>> => {
    try {
      // Send invitation using invitation service
      const result = await invitationService.sendInvitations({
        projectId,
        emails: [memberData.email],
        role: memberData.role,
        permissions: memberData.permissions,
        message: memberData.message,
      });

      if (result.success) {
        // Create a mock member response for immediate UI feedback
        const newMember: ProjectMember = {
          id: `pending-${Date.now()}`,
          userId: '',
          projectId,
          role: memberData.role,
          permissions: memberData.permissions,
          status: 'pending',
          assignedAt: new Date(),
          user: {
            id: '',
            firstName: '',
            lastName: '',
            email: memberData.email,
            expertise: [],
            isActive: true,
          },
        };

        return {
          success: true,
          data: newMember,
          message: 'Invitation sent successfully',
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to send invitation',
          errors: result.errors,
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add team member',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Update team member
  updateTeamMember: async (
    projectId: string,
    memberId: string,
    updates: UpdateTeamMemberData
  ): Promise<ApiResponse<ProjectMember>> => {
    try {
      const response = await api.put(`/projects/${projectId}/team/${memberId}`, updates);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'Team member updated successfully',
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to update team member',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update team member',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Remove team member
  removeTeamMember: async (projectId: string, memberId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await api.delete(`/projects/${projectId}/team/${memberId}`);

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Team member removed successfully',
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to remove team member',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to remove team member',
      };
    }
  },

  // Get team member invitations
  getInvitations: async (projectId: string): Promise<ApiResponse<TeamMemberInvitation[]>> => {
    try {
      const result = await invitationService.getProjectInvitations(projectId);

      if (result.success && result.data) {
        // Map invitation data to TeamMemberInvitation format
        const mappedInvitations: TeamMemberInvitation[] = result.data.map(inv => ({
          id: inv._id || inv.id,
          email: inv.email,
          role: inv.role,
          permissions: inv.permissions,
          invitedBy: inv.invitedBy,
          invitedAt: new Date(inv.invitedAt),
          expiresAt: new Date(inv.expiresAt),
          status: inv.status,
          message: inv.message,
        }));

        return {
          success: true,
          data: mappedInvitations,
        };
      } else {
        return result;
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch invitations',
      };
    }
  },

  // Resend invitation
  resendInvitation: async (
    _projectId: string,
    invitationId: string
  ): Promise<ApiResponse<void>> => {
    try {
      return await invitationService.resendInvitation(invitationId);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to resend invitation',
      };
    }
  },

  // Cancel invitation
  cancelInvitation: async (
    _projectId: string,
    invitationId: string
  ): Promise<ApiResponse<void>> => {
    try {
      return await invitationService.cancelInvitation(invitationId);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to cancel invitation',
      };
    }
  },

  // Search available users
  searchUsers: async (query: string, projectId?: string): Promise<ApiResponse<TeamUser[]>> => {
    try {
      // If we have a projectId, use the available users endpoint
      const endpoint = projectId
        ? `/projects/${projectId}/available-users?search=${encodeURIComponent(query)}`
        : `/auth/users/search?q=${encodeURIComponent(query)}`;

      const response = await api.get(endpoint);

      if (response.data.success) {
        const users: TeamUser[] = (response.data.data || []).map((user: any) => ({
          id: user._id || user.id,
          firstName: user.firstName || user.name?.split(' ')[0] || '',
          lastName: user.lastName || user.name?.split(' ').slice(1).join(' ') || '',
          email: user.email,
          title: user.companyType || '',
          company: user.companyName || '',
          expertise: [], // Could be enhanced later
          isActive: user.isActive ?? true,
        }));

        return {
          success: true,
          data: users,
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to search users',
        };
      }
    } catch (error: any) {
      console.error('Error searching users:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to search users',
      };
    }
  },

  // Check email availability and get user info
  checkEmailAvailability: async (
    email: string
  ): Promise<
    ApiResponse<{
      available: boolean;
      userExists: boolean;
      userId?: string;
      firstName?: string;
      lastName?: string;
      name?: string;
    }>
  > => {
    try {
      const response = await api.post('/auth/check-email', { email });

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to check email',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to check email',
      };
    }
  },
};
